/** Staff-to-staff messaging — the few decisions a client makes, in one testable place.
 *
 *  Everything else is the server's: who may see a conversation, what a soft-deleted
 *  message serves, whether a view is audited. These are the client-side rules that
 *  would otherwise sit inline in JSX and drift from the backend's.
 */

/** The clinic-wide channel. One channel, not a rooms feature. */
export const CLINIC_CHANNEL_ID = "clinic";

/** The id for a direct message between two people.
 *
 *  SORTED, and it must stay sorted: the server accepts exactly one spelling, so an
 *  unsorted id is a 404 rather than a second thread — but the reason the server is
 *  strict is that the unsorted version WOULD be a second thread, with each person
 *  holding half the conversation. The sort is the whole mechanism.
 */
export function dmConversationId(a, b) {
  return `dm:${[String(a), String(b)].sort().join(":")}`;
}

/** Is this the clinic channel? */
export function isChannel(conversationId) {
  return conversationId === CLINIC_CHANNEL_ID;
}

/** The other person in a DM, from the caller's point of view. */
export function otherParticipant(conversation, me) {
  const parts = conversation?.participants || [];
  return parts.find((p) => p !== me) || null;
}

/** May this message be edited or deleted?
 *
 *  Your own, and not already deleted. Mirrors the server, which answers 403 for
 *  somebody else's message and 400 for one that is already gone. Offering a button
 *  that can only produce an error is worse than not offering it.
 */
export function canEditMessage(message) {
  return Boolean(message?.mine) && !message?.deleted;
}

/** Total unread across the team conversations, for the Team tab's own count. */
export function teamUnread(conversations) {
  if (!Array.isArray(conversations)) return 0;
  return conversations.reduce((n, c) => n + (Number(c?.unread) || 0), 0);
}
